/** @jsxImportSource @emotion/react */
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import moment from 'moment'
import { css } from '@emotion/react'

import { useLazyGetDmeNotesQuery, useLazyGetPhysicianSchedulingNotesQuery } from 'store/services/notes'
import { Note } from 'store/types'

interface LazyDmeNotesPopoverProps {
  orderId: number
  children: ReactNode
  filterScheduling?: boolean
  physician?: boolean
}

const HIDE_DELAY_MS = 1000

export const LazyDmeNotesPopover = ({ orderId, children, filterScheduling, physician }: LazyDmeNotesPopoverProps) => {
  const dmeNotesQuery = useLazyGetDmeNotesQuery()
  const physicianNotesQuery = useLazyGetPhysicianSchedulingNotesQuery()
  const [trigger, { data, isFetching, isUninitialized, isError }] = physician ? physicianNotesQuery : dmeNotesQuery
  const [loaded, setLoaded] = useState(false)
  const [show, setShow] = useState(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const targetRef = useRef<HTMLSpanElement>(null)

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      setShow(false)
    }, HIDE_DELAY_MS)
  }, [clearHideTimeout])

  const handleMouseEnter = useCallback(() => {
    clearHideTimeout()
    setShow(true)
    if ((!loaded && (isUninitialized || !data)) || (isError && !isFetching)) {
      trigger({ orderId: String(orderId) })
      setLoaded(true)
    }
  }, [clearHideTimeout, loaded, isUninitialized, data, isError, isFetching, orderId, trigger])

  const handleMouseLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const handlePopoverMouseEnter = useCallback(() => {
    clearHideTimeout()
  }, [clearHideTimeout])

  const handlePopoverMouseLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const content = useMemo(() => {
    if (isError && !isFetching) {
      return (
        <div className="p-2">
          <p
            className="p-2 mb-0"
            css={css`
              background-color: #f8d7da;
              border: 1px solid #f5c2c7;
            `}
          >
            <small>Failed to load notes</small>
          </p>
        </div>
      )
    }

    if (isFetching || !data) {
      return (
        <div className="p-2">
          <small>Loading…</small>
        </div>
      )
    }

    let notes: Note[] = data || []
    if (filterScheduling === true) {
      notes = notes.filter((n) => n.type === 'scheduling')
    } else if (filterScheduling === false) {
      notes = notes.filter((n) => n.type !== 'scheduling')
    }

    // Sort notes in reverse chronological order (newest first)
    const sortedNotes = [...notes].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    return (
      <div 
        className="p-2"
        css={css`
          max-height: 60vh;
          overflow-y: auto;
        `}
      >
        {sortedNotes.map((n) => (
          <p
            key={n.uuid}
            className="p-2 mb-3"
            css={css`
              background-color: ${filterScheduling ? 'lightblue' : 'lightyellow'};
              border: 1px solid ${filterScheduling ? '#07B5FF80' : '#ffc10780'};
            `}
          >
            {moment(n.createdAt).format('MM/DD/YYYY HH:mm a')}:<br /> {n.text}
          </p>
        ))}
      </div>
    )
  }, [data, isFetching, isError, filterScheduling])

  const popover = (
    <Popover
      onMouseEnter={handlePopoverMouseEnter}
      onMouseLeave={handlePopoverMouseLeave}
    >
      <Popover.Header as="h3">{filterScheduling ? 'Recent Scheduling Notes' : 'Recent Notes'}</Popover.Header>
      {content}
    </Popover>
  )

  return (
    <>
      <span 
        ref={targetRef}
        onMouseEnter={handleMouseEnter} 
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter} 
        onBlur={handleMouseLeave}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      <Overlay
        show={show}
        target={targetRef.current}
        placement="left"
        transition={false}
      >
        {popover}
      </Overlay>
    </>
  )
}


